/**
 * CookieYes → Shopify Customer Privacy Bridge
 * Replaces: https://cdn-cookieyes.com/common/shopify.js
 *
 * Fix: The original script resets all consent to empty strings on every
 * page load before re-reading CookieYes state — causing the pixel sandbox
 * to initialize with cleared consent. This version reads the existing
 * CookieYes cookie first and never resets consent unnecessarily.
 */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ */
  /*  1. EARLY COOKIE WRITE                                             */
  /*     Runs synchronously before any Shopify script.                  */
  /*     Writes _tracking_consent so the pixel sandbox reads it on init */
  /* ------------------------------------------------------------------ */
  (function earlyConsentFromCookie() {
    var match = document.cookie.match(/cookieyes-consent=([^;]+)/);
    if (!match) return;

    try {
      var raw = decodeURIComponent(match[1]).split(",");
      var c = {};
      raw.forEach(function (pair) {
        var kv = pair.split(":");
        c[kv[0]] = kv[1];
      });

      // Only act if user has already interacted with the banner
      if (c.action !== "yes") return;

      var purposes = {
        p: c.functional !== "no",   // preferences
        a: c.analytics !== "no",    // analytics
        m: c.advertisement !== "no", // marketing
        t: c.advertisement !== "no"  // targeting / sale of data
      };

      var payload = JSON.stringify({
        con: { CMP: { a: "", m: "", p: "", s: "" } },
        v: "2.1",
        region: "",
        reg: "",
        purposes: purposes,
        display_banner: false,
        sale_of_data_region: true
      });

      document.cookie =
        "_tracking_consent=" +
        encodeURIComponent(payload) +
        ";path=/;SameSite=Lax";
    } catch (e) {
      /* fail silently — API-based flow below will handle it */
    }
  })();

  /* ------------------------------------------------------------------ */
  /*  2. HELPERS                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Read the cookieyes-consent cookie and return a categories object.
   * Returns null if cookie is missing or user hasn't interacted.
   */
  function readCookieYesCookie() {
    var match = document.cookie.match(/cookieyes-consent=([^;]+)/);
    if (!match) return null;

    try {
      var raw = decodeURIComponent(match[1]).split(",");
      var c = {};
      raw.forEach(function (pair) {
        var kv = pair.split(":");
        c[kv[0]] = kv[1];
      });
      if (c.action !== "yes") return null;

      return {
        analytics: c.analytics === "yes",
        advertisement: c.advertisement === "yes",
        functional: c.functional === "yes",
        performance: c.performance === "yes"
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Detect the active privacy law from CookieYes.
   * Falls back to the banner_load event detail if getCkyConsent is unavailable.
   */
  function getActiveLaw(eventDetail) {
    if (typeof window.getCkyConsent === "function") {
      var consent = window.getCkyConsent();
      if (consent && consent.activeLaw) return consent.activeLaw;
    }
    if (eventDetail && eventDetail.activeLaw) return eventDetail.activeLaw;
    return "";
  }

  /**
   * Build a categories object from a CookieYes event.
   * Handles both cookieyes_banner_load (has .categories)
   * and cookieyes_consent_update (has .accepted / .rejected).
   */
  function categoriesFromEvent(eventData) {
    if (!eventData || !eventData.detail) return null;

    var detail = eventData.detail;

    // banner_load format
    if (detail.categories) {
      return {
        analytics: !!detail.categories.analytics,
        advertisement: !!detail.categories.advertisement,
        functional: !!detail.categories.functional,
        performance: !!detail.categories.performance
      };
    }

    // consent_update format
    if (detail.accepted || detail.rejected) {
      var cats = {};
      (detail.accepted || []).forEach(function (k) { cats[k] = true; });
      (detail.rejected || []).forEach(function (k) { cats[k] = false; });
      return {
        analytics: !!cats.analytics,
        advertisement: !!cats.advertisement,
        functional: !!cats.functional,
        performance: !!cats.performance
      };
    }

    return null;
  }

  /**
   * Map CookieYes categories + active law → Shopify consent object
   * and call setTrackingConsent.
   */
  function pushToShopify(categories, activeLaw) {
    if (
      !categories ||
      !window.Shopify ||
      !window.Shopify.customerPrivacy ||
      typeof window.Shopify.customerPrivacy.setTrackingConsent !== "function"
    ) {
      return;
    }

    var shopifyConsent;

    if (activeLaw === "gdpr") {
      shopifyConsent = {
        analytics: categories.analytics,
        marketing: categories.advertisement,
        preferences: categories.functional
      };
    } else if (activeLaw === "ccpa") {
      // CCPA: Shopify only needs sale_of_data — true if all accepted
      var allAccepted = Object.keys(categories).every(function (k) {
        return categories[k] === true;
      });
      shopifyConsent = {
        sale_of_data: allAccepted
      };
    } else {
      // No specific law or unknown — send all signals
      shopifyConsent = {
        analytics: categories.analytics,
        marketing: categories.advertisement,
        preferences: categories.functional,
        sale_of_data: categories.advertisement
      };
    }

    window.Shopify.customerPrivacy.setTrackingConsent(shopifyConsent, function () {
      /* consent applied */
    });
  }

  /* ------------------------------------------------------------------ */
  /*  3. EVENT HANDLER                                                  */
  /*     Shared handler for banner_load, consent_update, and init       */
  /* ------------------------------------------------------------------ */
  function handleConsentEvent(eventData, isInitialLoad) {
    var categories = null;
    var activeLaw = "";

    if (isInitialLoad) {
      // Read existing cookie — do NOT reset to empty strings
      categories = readCookieYesCookie();
      if (!categories) return; // user hasn't interacted yet, let Shopify defaults apply
      activeLaw = getActiveLaw(null);
    } else {
      categories = categoriesFromEvent(eventData);
      if (!categories) return;
      activeLaw = getActiveLaw(eventData ? eventData.detail : null);
    }

    pushToShopify(categories, activeLaw);
  }

  /* ------------------------------------------------------------------ */
  /*  4. INITIALIZE — Load consent-tracking-api then bind events        */
  /* ------------------------------------------------------------------ */
  window.addEventListener("load", function () {
    if (!window.Shopify || !window.Shopify.loadFeatures) return;

    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) return;

        // Apply stored consent (without resetting)
        handleConsentEvent(null, true);

        // Banner loaded on this page (fires every page)
        document.addEventListener("cookieyes_banner_load", function (e) {
          handleConsentEvent(e, false);
        });

        // User interacted with the banner
        document.addEventListener("cookieyes_consent_update", function (e) {
          handleConsentEvent(e, false);
        });
      }
    );
  });
})();